import { Mail, MapPin, Radio, ShieldCheck, Video } from 'lucide-react'
import { ButtonLink, Card } from '@/components/play'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { SitePage } from '@/components/SitePage'

const sections = [
  {
    title: 'What StreamBridge collects',
    body: [
      'Account and contact details you provide, such as your name, email address, organization, and support messages.',
      'Event and stream details, including event names, camera names, stream keys, invite links, quality settings, and operational status.',
      'Live camera data when you choose to go live: the video and sound the camera sends, and the session information needed to carry the feed.',
      'Location when you allow it. The camera app sends the phone’s position about once a second while the camera screen is open, and keeps sending it while you’re live with the screen locked.',
      'A random install ID the camera app creates, so StreamBridge can recognize the phone when it connects a camera. It isn’t linked to your Google or Apple account.',
      'Camera health reports every 5 seconds while you’re live, such as bitrate, round-trip time, packet loss, and reconnects.',
      'Technical data such as device type, app version, browser or operating system, IP address, logs, crash data, performance diagnostics, and how the website is used.',
    ],
  },
  {
    title: 'How we use it',
    body: [
      'To create and run race coverage, including camera invites, live feeds, course maps, viewer pages, and recovery from connection problems.',
      'To keep StreamBridge reliable, secure, and easy to support during live events.',
      'To communicate with event teams about setup, support, product updates, and service changes.',
      'To improve the product by understanding reliability, quality, and usage patterns without selling personal data or using it for third-party ad tracking.',
    ],
  },
  {
    title: 'Who sees it',
    body: [
      'Anyone following an event can watch its live cameras on the event’s public page and see where they are on the course map. Sound plays there only when the host turns it on. A host can make a camera private, which hides it from the public event page.',
      'Event organizers and production teams may see the cameras, locations, streams, and operational information needed to cover their event.',
      'Service providers that host, process, secure, monitor, or support StreamBridge process data for us under contract, such as Google Cloud for hosting, LiveKit for live video and data, Sentry for error monitoring, and PostHog for website analytics.',
      'The camera app’s QR scanner uses Google’s ML Kit, which reads codes on the phone. Google collects diagnostics from it, such as the phone model, operating system and app version, a per-install identifier, and performance figures, under Google’s privacy policy.',
      'We may share information if required by law, to protect StreamBridge and our users, or during a business transfer such as a merger or acquisition.',
      'We do not sell personal data and we do not use personal data for third-party advertising.',
    ],
  },
  {
    title: 'How long we keep it',
    body: [
      'Camera health reports are deleted after 30 days.',
      'For location, we keep only each camera’s latest position, replaced as new positions arrive. Positions sent live to the event page aren’t stored.',
      'StreamBridge doesn’t record camera video or sound. Hosts who bring a camera into OBS or another tool may record it there.',
      'Account, event, and camera details are kept while you use StreamBridge. Ask us to delete them and we will, unless we need to keep something to meet a legal obligation or prevent abuse.',
      'Logs and other technical data are kept only as long as we need them to run and fix StreamBridge.',
      'On the phone, the camera app keeps its install ID, its camera sessions, your quality choice, and the last 10 cameras you went live on. Uninstalling the app deletes them.',
    ],
  },
  {
    title: 'How we protect it',
    body: [
      'The website, the StreamBridge API, and LiveKit use encrypted connections (TLS).',
      'Camera video and sound travel to StreamBridge over SRT without encryption, so someone on the same network could intercept a stream and its stream key.',
      'The camera app stores its camera sessions encrypted, with the key held in the phone’s secure storage.',
    ],
  },
  {
    title: 'Your choices',
    body: [
      'You can deny camera, microphone, or location permissions. The camera app needs the camera and microphone to go live. Without location it still streams, but the camera won’t appear on the course map.',
      'You can ask us to access, correct, export, or delete personal information where applicable.',
      'You can contact us about privacy at hello@streambridge.live.',
    ],
  },
]

const highlights = [
  { icon: Video, label: 'Camera and microphone are used only when you choose to stream.' },
  { icon: MapPin, label: 'Live cameras appear on the event’s public page and course map.' },
  { icon: Radio, label: 'Camera health reports are deleted after 30 days.' },
  { icon: ShieldCheck, label: 'No sale of personal data. No third-party ad tracking.' },
]

/** Privacy (/privacy): what StreamBridge collects, how it's used, who sees it and the choices people have. */
export function PrivacyPage() {
  useDocumentTitle('Privacy · StreamBridge')
  return (
    <SitePage mainId="privacy-main" className="text-neutral-10" mainClassName="px-6 py-16">
      <div className="mx-auto max-w-4xl">
        <div className="mb-12">
          <p className="mb-4 text-size-xs font-medium text-neutral-7">Privacy policy</p>
          <h1 className="text-4xl font-black sm:text-5xl">
            We collect what we need to cover the race.
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-neutral-7">
            StreamBridge brings cameras from the course to everyone following the race. Here’s what we collect, how we
            use and protect it, how long we keep it and the choices you have.
          </p>
          <p className="mt-4 max-w-2xl text-neutral-7">
            This policy is from StreamBridge, a Delaware corporation. It covers the StreamBridge website and our camera
            apps, including StreamBridge Camera, which we publish on Google Play under our LiveLabs Ventures developer
            account.
          </p>
          <p className="mt-4 text-sm text-neutral-7">Last updated <time dateTime="2026-10-24">October 24, 2026</time></p>
        </div>

        {/* role="list" because VoiceOver drops list semantics from an unstyled list. */}
        <ul className="mb-12 grid gap-4 sm:grid-cols-2" role="list" aria-label="In short">
          {highlights.map((item) => (
            <li key={item.label} className="flex gap-3 rounded-lg bg-neutral-2 p-4">
              <item.icon aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-neutral-10" />
              <p className="text-sm text-neutral-8">{item.label}</p>
            </li>
          ))}
        </ul>

        <div className="space-y-10">
          {sections.map((section) => (
            <section key={section.title}>
              <h2 className="mb-4 text-2xl font-bold">{section.title}</h2>
              <ul className="space-y-3" role="list">
                {section.body.map((item) => (
                  <li key={item} className="flex gap-3 text-neutral-8">
                    <span aria-hidden="true" className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-10" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <Card as="section" title="Contact us" description="Questions about this policy or your data?" className="mt-12">
          <ButtonLink href="mailto:hello@streambridge.live">
            <Mail aria-hidden="true" />
            hello@streambridge.live
          </ButtonLink>
        </Card>
      </div>
    </SitePage>
  )
}
